import {
  AT_HOME_SITUATION,
  DOCS_FORM_NAMES,
  GUARDIAN,
  HOME_CARE_SITUATION,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_FORM_NAMES,
} from "core/consts";
import { getUnixTime } from "date-fns";
import {
  ControlledCheckbox,
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
  ControlledTextarea,
} from "ds/ui";
import { FormFieldConfig } from "..";
import { SourceWrapper } from "../../utils";
import {
  CommunicationInGermanPossibleField,
  PreferredClinicContactedField,
} from "./fields";

export const GKVGeneralFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.GKV_GENERAL
>["sections"] = [
  {
    fields: {
      insurance_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson
                .insuranceNumber
            }
            name="insurance_number"
            placeholder=" "
          />
        ),
        position: 9,
      },
      patient_birthdate: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledDateInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.insuredPerson.dateOfBirth
            }
            maxValue={getUnixTime(new Date())}
            name="patient_birthdate"
          />
        ),
        position: 8,
      },
      patient_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.city}
            name="patient_city"
            placeholder=" "
          />
        ),
        position: 6,
      },
      patient_first_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.firstName}
            name="patient_first_name"
            placeholder=" "
          />
        ),
        position: 1,
      },
      patient_housenumber: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="patient_housenumber"
            placeholder=" "
          />
        ),
        position: 4,
      },
      patient_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.lastName}
            name="patient_last_name"
            placeholder=" "
          />
        ),
        position: 2,
      },
      patient_phone_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.insuredPerson.phone}
            name="patient_phone_number"
            placeholder=" "
            type="tel"
          />
        ),
        position: 7,
      },
      patient_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="patient_street"
            placeholder=" "
          />
        ),
        position: 3,
      },
      patient_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.zipcode}
            name="patient_zipcode"
            placeholder=" "
          />
        ),
        position: 5,
      },
    },
    position: 1,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.titleDocs,
  },
  {
    fields: {
      domestic_situation_is_safe: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .currentHomeCareEnsured
              }
              name="domestic_situation_is_safe"
              options={options}
            />
          );
        },
        position: 10,
      },
      general_practitioner_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpCity}
            name="general_practitioner_city"
            placeholder=" "
          />
        ),
        position: 18,
      },
      general_practitioner_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="general_practitioner_first_name"
            placeholder=" "
          />
        ),
        position: 12,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.questionGp,
      },
      general_practitioner_housenumber: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="general_practitioner_housenumber"
            placeholder=" "
          />
        ),
        position: 16,
      },
      general_practitioner_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.lastName}
            name="general_practitioner_last_name"
            placeholder=" "
          />
        ),
        position: 13,
      },
      general_practitioner_phone_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpPhoneNumber}
            name="general_practitioner_phone_number"
            placeholder=" "
            type="tel"
          />
        ),
        position: 19,
      },
      general_practitioner_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="general_practitioner_street"
            placeholder=" "
          />
        ),
        position: 15,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.subtitleAddressGp,
      },
      general_practitioner_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpZipCode}
            name="general_practitioner_zipcode"
            placeholder=" "
          />
        ),
        position: 17,
      },
      insured_details_application_insurance_benefits: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .questionNeedForCare
              }
              name="insured_details_application_insurance_benefits"
              options={options}
            />
          );
        },
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.titleNeedForCare,
      },
      insured_details_guardian_1_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.city}
            name="insured_details_guardian_1_city"
            placeholder=" "
          />
        ),
        position: 26,
      },
      insured_details_guardian_1_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="insured_details_guardian_1_first_name"
            placeholder=" "
          />
        ),
        position: 21,
      },
      insured_details_guardian_1_house_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="insured_details_guardian_1_house_number"
            placeholder=" "
          />
        ),
        position: 24,
      },
      insured_details_guardian_1_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.lastName}
            name="insured_details_guardian_1_last_name"
            placeholder=" "
          />
        ),
        position: 22,
      },
      insured_details_guardian_1_phone: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpPhoneNumber}
            name="insured_details_guardian_1_phone"
            placeholder=" "
            type="tel"
          />
        ),
        position: 27,
      },
      insured_details_guardian_1_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="insured_details_guardian_1_street"
            placeholder=" "
          />
        ),
        position: 23,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.subtitleAddressGp,
      },
      insured_details_guardian_1_type: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.patientForms.generalForm.sectionI.optionCarer,
              value: GUARDIAN.CARER,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionHealthCareRep,
              value: GUARDIAN.HEALTH_CARE_REP,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI.optionLegalRep,
              value: GUARDIAN.LEGAL_REP,
            },
          ];
          return (
            <ControlledRadioGroup
              name="insured_details_guardian_1_type"
              options={options}
            />
          );
        },
        position: 20,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.questionGuardian,
      },
      insured_details_guardian_1_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.zipcode}
            name="insured_details_guardian_1_zipcode"
            placeholder=" "
          />
        ),
        position: 25,
      },
      insured_details_guardian_2_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.city}
            name="insured_details_guardian_2_city"
            placeholder=" "
          />
        ),
        position: 33,
      },
      insured_details_guardian_2_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="insured_details_guardian_2_first_name"
            placeholder=" "
          />
        ),
        position: 28,
      },
      insured_details_guardian_2_house_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.number}
            name="insured_details_guardian_2_house_number"
            placeholder=" "
          />
        ),
        position: 31,
      },
      insured_details_guardian_2_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.lastName}
            name="insured_details_guardian_2_last_name"
            placeholder=" "
          />
        ),
        position: 29,
      },
      insured_details_guardian_2_phone: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.generalForm.sectionI.gpPhoneNumber}
            name="insured_details_guardian_2_phone"
            placeholder=" "
            type="tel"
          />
        ),
        position: 34,
      },
      insured_details_guardian_2_street: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.street}
            name="insured_details_guardian_2_street"
            placeholder=" "
          />
        ),
        position: 30,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.subtitleAddressGp,
      },
      insured_details_guardian_2_type: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.patientForms.generalForm.sectionI.optionCarer,
              value: GUARDIAN.CARER,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionHealthCareRep,
              value: GUARDIAN.HEALTH_CARE_REP,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI.optionLegalRep,
              value: GUARDIAN.LEGAL_REP,
            },
          ];
          return (
            <ControlledRadioGroup
              name="insured_details_guardian_2_type"
              options={options}
            />
          );
        },
        position: 27,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.questionGuardian,
      },
      insured_details_guardian_2_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.address.zipcode}
            name="insured_details_guardian_2_zipcode"
            placeholder=" "
          />
        ),
        position: 32,
      },
      living_lift: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .livingSituationElevator
              }
              name="living_lift"
              options={options}
            />
          );
        },
        position: 7,
      },
      living_lift_description: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationFloor
            }
            name="living_lift_description"
            placeholder=" "
          />
        ),
        position: 6,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI
            .questionLivingSituation,
      },
      living_situation: {
        input: ({
          setSourceSelected,
          skipRequiredValidation,
          sourceSelected,
          translations,
        }) => {
          const homeOptions = [
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionAtHomeAlone,
              value: AT_HOME_SITUATION.ALONE,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionAtHomeWithRelatives,
              value: AT_HOME_SITUATION.RELATIVES,
            },
          ];

          const homeCareSituationOptions = [
            {
              label:
                translations.patientForms.generalForm.sectionI.optionAtHome,
              subForm: () => (
                <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
                  fieldName="living_situation_at_home"
                  isIconOnly
                  setSourceSelected={setSourceSelected}
                  sourceSelected={sourceSelected}
                >
                  <ControlledRadioGroup
                    label={
                      translations.patientForms.generalForm.sectionI
                        .accessibilityAtHomeSituation
                    }
                    name="living_situation_at_home"
                    options={homeOptions}
                  />
                </SourceWrapper>
              ),
              value: HOME_CARE_SITUATION.HOME,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI
                  .optionAssistedLiving,
              value: HOME_CARE_SITUATION.ASSISTED_LIVING,
            },
            {
              label:
                translations.patientForms.generalForm.sectionI.optionCareHome,
              value: HOME_CARE_SITUATION.CARE_HOME,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI.subtitleHomeCare
              }
              name="living_situation"
              options={homeCareSituationOptions}
            />
          );
        },
        position: 9,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.questionHomeCare,
      },
      living_special: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationSpecifications
            }
            name="living_special"
            placeholder=" "
          />
        ),
        position: 8,
      },
      reason_for_reha_clinic: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .labelSpecialRequirements
            }
            name="reason_for_reha_clinic"
            placeholder=" "
          />
        ),
        position: 35,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI
            .questionSpecialRequirements,
      },
      retirement_earning_capacity_company: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            name="retirement_earning_capacity_company"
            placeholder=" "
          />
        ),
        position: 5,
      },
      retirement_pension_applied: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .subtitleRetirementPension
              }
              name="retirement_pension_applied"
              options={options}
            />
          );
        },
        position: 2,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI
            .titlePensionApplication,
      },
      retirement_pension_company: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            name="retirement_pension_company"
            placeholder=" "
          />
        ),
        position: 3,
      },
      retirement_pension_earning_capacity_applied: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .subtitlePensionReducedEarning
              }
              name="retirement_pension_earning_capacity_applied"
              options={options}
            />
          );
        },
        position: 4,
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.title,
  },
  {
    fields: {
      declaration_of_intent_confirmed: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledCheckbox
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.declarationOfIntentProvided
            }
            name="declaration_of_intent_confirmed"
            size="md"
          />
        ),
        position: 1,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionI.signatureDeclaration,
      },
    },
    position: 3,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.additionalInfoNote,
  },
  {
    description: ({ translations }) =>
      translations.patientForms.generalForm.sectionI.consentToDataTransferText,
    fields: {
      data_transfer_consent_given: {
        input: ({ skipRequiredValidation, translations }) => {
          const options = [
            {
              label: translations.patientForms.generalForm.consentDataProvided,
              value: PREDICAMENT_STATE_YES,
            },
            {
              label:
                translations.patientForms.generalForm.sectionTwo
                  .consentDataNotProvided,
              value: PREDICAMENT_STATE_NO,
            },
          ];
          return (
            <ControlledRadioGroup
              isRequired={!skipRequiredValidation}
              label={
                translations.patientForms.generalForm.sectionI
                  .consentToDataTransferSignature
              }
              name="data_transfer_consent_given"
              options={options}
            />
          );
        },
        position: 1,
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionI
        .consentToDataTransferHeading,
  },
  {
    fields: {
      case_id: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.patientForms.generalForm.sectionTwo.patientId}
            name="case_id"
            placeholder=" "
          />
        ),
        position: 2,
      },
      communication_in_german_possible: {
        input: CommunicationInGermanPossibleField,
        position: 7,
      },
      prefered_clinic_contacted: {
        input: PreferredClinicContactedField,
        position: 8,
      },
      site_id_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isDisabled
            isRequired={!skipRequiredValidation}
            label={translations.careseeker.siteIdNumber}
            name="site_id_number"
            placeholder=" "
          />
        ),
        position: 1,
      },
      social_worker_fax_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.generalForm.sectionTwo.socialWorkerFax
            }
            name="social_worker_fax_number"
            placeholder=" "
            type="tel"
          />
        ),
        position: 6,
      },
      social_worker_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.people.firstName}
            name="social_worker_first_name"
            placeholder=" "
          />
        ),
        position: 3,
        subTitle: ({ translations }) =>
          translations.patientForms.generalForm.sectionTwo.detailsSocialWorker,
      },
      social_worker_last_name: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={translations.people.lastName}
            name="social_worker_last_name"
            placeholder=" "
          />
        ),
        position: 4,
      },
      social_worker_phone_number: {
        input: ({ skipRequiredValidation, translations }) => (
          <ControlledInput
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionTwo.socialWorkerPhone
            }
            name="social_worker_phone_number"
            placeholder=" "
            type="tel"
          />
        ),
        position: 5,
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.generalForm.sectionTwo.title,
  },
];
